import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_CONFIG, hasIncompleteLegalConfig } from '../config/legal';
import './LegalPage.css';

export default function TermsPage() {
  const { productName, operatorName, contactEmail, governingState, effectiveDate } = LEGAL_CONFIG;

  return (
    <main className="legal-page">
      <div className="legal-page__inner">
        <Link className="legal-page__brand" to="/">{productName}</Link>
        <header className="legal-page__header">
          <h1>Terms of Use</h1>
          <p className="legal-page__meta">Effective: {effectiveDate}</p>
          <p className="legal-page__intro">
            These Terms of Use govern access to {productName}, a college-planning software service operated by {operatorName}.
            By creating an account or using the service, you agree to these Terms and acknowledge the <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </header>

        {hasIncompleteLegalConfig && (
          <aside className="legal-page__draft-warning" role="note">
            Draft notice: replace the bracketed operator, contact, address, and state information before public launch and have qualified counsel review this document.
          </aside>
        )}

        <section>
          <h2>1. Eligibility</h2>
          <p>You must be at least 13 years old to use {productName}. If you are under the age of legal majority where you live, you may use the service only with permission from a parent or legal guardian who agrees to these Terms on your behalf. The service is currently intended for users in the United States.</p>
          <p>Do not create an account if you are under 13. We may suspend or delete an account if we reasonably believe its user does not meet these requirements.</p>
        </section>

        <section>
          <h2>2. The Service</h2>
          <p>{productName} provides organizational tools for college research and applications, including dashboards, college lists, academic-profile records, tasks, calendars, reminders, links, and application-progress tracking. Features may change as the service develops.</p>
          <p>{productName} is an independent planning tool. It is not a college, school, government agency, admissions office, financial institution, testing organization, or professional counseling service, and it is not endorsed by those organizations unless expressly stated.</p>
        </section>

        <section>
          <h2>3. Accounts and Security</h2>
          <p>You must provide accurate account information and keep your sign-in credentials secure. You are responsible for activity performed through your account and should notify us promptly if you believe it has been accessed without permission.</p>
          <p>You may sign in using email and password or an available third-party sign-in provider. Your use of a third-party provider is also governed by that provider’s terms.</p>
        </section>

        <section>
          <h2>4. Your Information and Content</h2>
          <p>You retain ownership of information you enter into {productName}, including notes, lists, tasks, academic information, activities, awards, links, and application records (“User Content”). You give us a limited, nonexclusive license to host, process, reproduce, and display User Content only as reasonably necessary to operate, secure, maintain, and improve the service.</p>
          <p>You are responsible for ensuring that you have permission to enter User Content and that it does not violate another person’s privacy, intellectual-property, or other rights. Avoid storing passwords, Social Security numbers, payment-card details, medical records, or other information the service does not request.</p>
        </section>

        <section>
          <h2>5. College Information and Deadlines</h2>
          <p>College facts may come from public sources such as the U.S. Department of Education College Scorecard. Public data, user-entered dates, generated schedules, and reminders may be incomplete, delayed, or inaccurate.</p>
          <p>You are solely responsible for confirming admissions requirements, costs, financial-aid requirements, application status, and deadlines with each college or other official source. A reminder, task, or submitted marker in {productName} is not proof that an application or document was received.</p>
        </section>

        <section>
          <h2>6. No Professional Advice or Outcome Guarantee</h2>
          <p>The service provides organizational information, not educational, admissions, legal, tax, financial, mental-health, or other professional advice. {productName} does not guarantee admission, scholarships, financial aid, application completion, or any other outcome.</p>
        </section>

        <section>
          <h2>7. Acceptable Use</h2>
          <p>You may not:</p>
          <ul>
            <li>Use the service unlawfully, fraudulently, or to harm another person.</li>
            <li>Access or attempt to access another user’s account or data.</li>
            <li>Interfere with the service, bypass security controls, probe for vulnerabilities, or introduce malicious code.</li>
            <li>Scrape, copy, reverse engineer, or commercially exploit the service except where applicable law expressly permits it.</li>
            <li>Use automated systems in a way that unreasonably burdens the service or its providers.</li>
            <li>Impersonate another person or misrepresent an affiliation with a school, college, or organization.</li>
          </ul>
        </section>

        <section>
          <h2>8. Our Intellectual Property</h2>
          <p>The service, design, software, branding, and original content provided by {productName}, excluding User Content and third-party materials, are owned by or licensed to the operator and protected by applicable law. These Terms grant you a personal, limited, revocable, nontransferable right to use the service for its intended purpose.</p>
        </section>

        <section>
          <h2>9. Third-Party Services and Links</h2>
          <p>The service relies on or may link to third parties, including Google Firebase, Google Sign-In, public government data, college websites, and user-saved external resources. We do not control third-party services and are not responsible for their content, availability, security, or practices. Review their terms and privacy policies before using them.</p>
        </section>

        <section>
          <h2>10. Availability, Changes, and Beta Features</h2>
          <p>We may add, modify, suspend, or discontinue features. During an MVP or beta period, the service may contain defects, experience interruptions, or lose functionality. We do not promise uninterrupted availability or that every error will be corrected.</p>
          <p>We will use reasonable care with account data, but you should retain independent copies of information that is critical to an application.</p>
        </section>

        <section>
          <h2>11. Suspension and Termination</h2>
          <p>You may stop using the service at any time and may request account deletion as described in the Privacy Policy. We may suspend or terminate access when reasonably necessary to protect users or the service, comply with law, address prohibited conduct, or discontinue the service.</p>
          <p>Sections that by their nature should survive termination, including ownership, disclaimers, liability limitations, and dispute provisions, will survive.</p>
        </section>

        <section>
          <h2>12. Disclaimers</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” WE DISCLAIM IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. Some jurisdictions do not allow certain disclaimers, so portions of this section may not apply to you.</p>
        </section>

        <section>
          <h2>13. Limitation of Liability</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE OPERATOR WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST DATA, LOST OPPORTUNITIES, MISSED DEADLINES, OR ADMISSIONS OR FINANCIAL-AID OUTCOMES ARISING FROM USE OF THE SERVICE.</p>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE OPERATOR’S TOTAL LIABILITY ARISING FROM THE SERVICE WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID FOR THE SERVICE DURING THE 12 MONTHS BEFORE THE CLAIM OR $100. This limitation does not apply where prohibited by law.</p>
        </section>

        <section>
          <h2>14. Governing Law and Disputes</h2>
          <p>These Terms are governed by the laws of {governingState}, without regard to conflict-of-law principles. Any dispute not resolved informally will be brought in a court with proper jurisdiction in that state, unless applicable consumer law requires otherwise.</p>
          <p>Before filing a claim, you agree to contact us and attempt to resolve the issue informally for at least 30 days. This draft does not include a mandatory arbitration or class-action waiver; obtain legal advice before adding either provision.</p>
        </section>

        <section>
          <h2>15. Changes to These Terms</h2>
          <p>We may update these Terms as the service or law changes. We will post the revised Terms with a new effective date and provide additional notice when a change materially affects users’ rights. Continued use after the effective date of revised Terms constitutes acceptance where permitted by law.</p>
        </section>

        <section className="legal-page__contact">
          <h2>16. Contact</h2>
          <p>Questions about these Terms may be sent to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.</p>
        </section>
      </div>
    </main>
  );
}
